import React, { useContext } from "react";
import useSWR from "swr";
import { AuthContext } from "@/App.jsx";
import { useLocation, useHistory, useParams, Switch, Route, Redirect } from "react-router-dom";
import Loader from "Components/loader/Loader";
import { displayFormattedDate } from "Utils/dateFormats";
import { Icon, disabledDomain } from "Icons/index";
import t from "Utils/translation.js";
import ButtonBack from "Components/buttonBack/ButtonBack";
import Button from "Components/button/Button";
import DetailInfo from "Components/detailInfo/DetailInfo";
import DeliveryDomainStatusBadge from "Components/deliveryDomainStatusBadge/DeliveryDomainStatusBadge";
import cx from "classnames";
import DeliveryDomainAccounts from "./tabs/Accounts";
import DeliveryDomainVerification from "./tabs/Verification";
import DeliveryDomainApproval from "./tabs/Approval";
import ShowInfoButton from "Components/showInfoButton/ShowInfoButton";
import useToggleDetailsInfo from "Utils/useToggleDetailsInfo";
import WarningBanner from "Components/warningBanner/WarningBanner";
import s from "../delivery.scss";

const DeliveryDomainsShowScreen = (props) => {
  const location = useLocation();
  const history = useHistory();
  const { domainCode } = useParams();
  const { isLoggedUserAdmin } = useContext(AuthContext);
  const { data, error, mutate } = useSWR("/delivery/domains/" + domainCode);
  const { isInfoVisible, isInfoToggled, toggleInfo } = useToggleDetailsInfo();

  const activeTab = location.pathname.split("/").slice(-1)[0];

  if (error && error.status === 404) {
    history.replace("/not-found");
  }

  if (!data || error) {
    return <Loader {...props} />;
  }

  const domain = data.resources[0];

  const showTab = (slug) => {
    const path = location.pathname.split("/");
    path[path.length - 1] = slug;
    history.push(path.join("/"));
  };

  const goBack = () => {
    history.push("/delivery/domains");
  };

  const basePath = isLoggedUserAdmin()
    ? "/customers/:customerCode/delivery/domains/:domainCode"
    : "/delivery/domains/:domainCode";

  return (
    <div className="flexColumnContainer">
      {domain.status && domain.dns_verification_status == "not_verified" && (
        <div className={s.warningHMsg}>
          <div className={s.wrapHMsg}>
            <div className={s.textHMsg}>{t("infoMsg.delivery.unhealthy.text")}</div>
            <div className={s.descHMsg}>{t("infoMsg.delivery.unhealthy.desc")}</div>
          </div>
          <Button className={s.btnHMsg} color="outline" type="button" onClick={() => showTab("verification")}>
            button.showDNSConfig
          </Button>
        </div>
      )}

      <div className={s.navigation}>
        <ButtonBack onClick={goBack} />
      </div>

      <div className={s.header}>
        <div className={s.titleContainer}>
          <div className="sectionTitle">{domain.name}</div>
          <DeliveryDomainStatusBadge domain={domain} asLabel />
          <ShowInfoButton onClick={toggleInfo} isInfoOpen={isInfoToggled} />
        </div>
        <div className="sectionDesc" />

        {domain.accounts_count === 0 && domain.approval_status === "approved" && (
          <div className="mt-3">
            <WarningBanner color="light-red" msg={t("delivery.domains.show.warning.noAccounts.msg")} />
          </div>
        )}

        {isInfoVisible && (
          <div className={s.info}>
            <DetailInfo label={t("detailInfo.label.code")} info={domain.code} inLine />

            {domain.manager_fullname && (
              <DetailInfo label={t("detailInfo.label.manager")} info={domain.manager_fullname || "-"} inLine />
            )}

            <DetailInfo
              label={t("detailInfo.label.created")}
              info={domain.created_at && displayFormattedDate(domain.created_at)}
              inLine
            />
          </div>
        )}
      </div>

      <div className={s.settings}>
        <div className={s.scrollTabs}>
          <div className={s.tabs}>
            <div
              className={cx(s.itemTab, { [s.active]: activeTab === "accounts" })}
              onClick={() => showTab("accounts")}
            >
              {t("tab.connectedAccounts")}
            </div>
            <div
              className={cx(s.itemTab, { [s.active]: activeTab === "verification" })}
              onClick={() => showTab("verification")}
            >
              {t("tab.verify")}
            </div>
            <div
              className={cx(s.itemTab, {
                [s.active]: activeTab === "approval",
              })}
              onClick={() => showTab("approval")}
            >
              {t("tab.approval")}
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 px-4 mb-12 overflow-y-scroll md:mb-0">
        <Switch>
          <Route
            exact
            path={`${basePath}/accounts`}
            render={() => <DeliveryDomainAccounts accounts={domain.accounts} />}
          />
          <Route
            exact
            path={`${basePath}/verification`}
            render={() => (
              <DeliveryDomainVerification domain={domain} isLoggedUserAdmin={isLoggedUserAdmin()} onUpdate={mutate} />
            )}
          />
          <Route
            exact
            path={`${basePath}/approval`}
            render={() => (
              <DeliveryDomainApproval isLoggedUserAdmin={isLoggedUserAdmin()} domain={domain} onUpdate={mutate} />
            )}
          />
          <Redirect from={basePath} to={`${basePath}/accounts`} />
        </Switch>
      </div>

      {domain.status === "disabled" && (
        <div className={s.wrapFulldMsg}>
          <div className={s.illustrationMsg}>
            <Icon svg={disabledDomain} width="10rem" height="10rem" />
            <div>{t("alert.cannotEditWhileDisabled")}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeliveryDomainsShowScreen;
